/**
 * Compress and resize image to 320x320px and convert to base64
 * @param file - Image file to compress
 * @param maxSize - Maximum dimension (default 320px)
 * @returns Promise<string> - Base64 encoded image
 */
export const compressImageToBase64 = (
  file: File,
  maxSize: number = 320
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // Create canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        
        // Set canvas size to 320x320
        canvas.width = maxSize;
        canvas.height = maxSize;
        
        // Calculate scaling to fit image in square while maintaining aspect ratio
        const scale = Math.min(maxSize / img.width, maxSize / img.height);
        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;
        
        // Center the image
        const x = (maxSize - scaledWidth) / 2;
        const y = (maxSize - scaledHeight) / 2;
        
        // Fill background with white
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, maxSize, maxSize);
        
        // Draw image
        ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
        
        // Convert to base64
        const base64 = canvas.toDataURL('image/jpeg', 0.85);
        resolve(base64);
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      
      img.src = e.target?.result as string;
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Validate image file
 * @param file - File to validate
 * @param maxSizeMB - Maximum file size in MB (default 5MB)
 * @returns Error message or null if valid
 */
export const validateImageFile = (
  file: File,
  maxSizeMB: number = 5
): string | null => {
  // Check file type
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (!validTypes.includes(file.type)) {
    return 'Format file harus JPG, PNG, atau WebP';
  }
  
  // Check file size
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return `Ukuran file maksimal ${maxSizeMB}MB`;
  }
  
  return null;
};
